import * as React from "react"
import { useTranslation } from "react-i18next"
import { Calendar, Clock } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  buildLayout,
  caretAfterDigits,
  isoToText,
  maskText,
  normalizeIso,
  placeholderOf,
  textToIso,
  type DateFieldType,
} from "@/lib/dateFieldMask"

export const FIELD_BOX = `
  flex h-[var(--density-field-h)] w-full rounded-md border border-input bg-transparent px-[var(--density-field-px)] py-1 text-sm shadow-sm
  transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium
  placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2
  focus-visible:ring-primary-200 focus-visible:border-primary disabled:cursor-not-allowed
  disabled:opacity-50
`

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> & { type: DateFieldType }

/**
 * Date, time and date-time field in the order of the app's language.
 *
 * The native field takes its order from the browser or OS language (Firefox in
 * English shows mm/dd/yyyy on a Brazilian community), so the visible part is a
 * masked text field and the native input only stays, invisible, to provide the
 * calendar through the icon. The contract is the native one: `value` and
 * `e.target.value` are ISO, '' until the typed text is a complete, real date.
 */
export const DateFieldInput = React.forwardRef<HTMLInputElement, Props>(
  ({ type, className, value, onChange, onBlur, min, max, step, name, disabled, readOnly, required, placeholder, style, ...rest }, ref) => {
    const { t, i18n } = useTranslation("common")
    const locale = i18n.resolvedLanguage || i18n.language || "en-US"
    const layout = React.useMemo(() => buildLayout(type, locale), [type, locale])
    const iso = normalizeIso(type, value)
    const minIso = normalizeIso(type, min) || undefined
    const maxIso = normalizeIso(type, max) || undefined

    const [text, setText] = React.useState(() => isoToText(layout, type, iso))
    const textRef = React.useRef<HTMLInputElement | null>(null)
    const nativeRef = React.useRef<HTMLInputElement | null>(null)
    const caret = React.useRef<number | null>(null)

    React.useImperativeHandle(ref, () => textRef.current as HTMLInputElement)

    /* The caller's value wins whenever it no longer matches what is typed: a
       reset, a load, a pick in the calendar or a language switch. A partial
       text stands for '' and survives a caller that keeps ''. */
    React.useEffect(() => {
      setText((current) => (textToIso(layout, type, current) === iso ? current : isoToText(layout, type, iso)))
    }, [iso, layout, type])

    React.useLayoutEffect(() => {
      if (caret.current === null || !textRef.current) return
      textRef.current.setSelectionRange(caret.current, caret.current)
      caret.current = null
    }, [text])

    const parsed = textToIso(layout, type, text)
    const invalid = text !== "" && !parsed
    const outOfRange = !!parsed && ((!!minIso && parsed < minIso) || (!!maxIso && parsed > maxIso))
    React.useEffect(() => {
      textRef.current?.setCustomValidity(invalid ? t("dateInput.invalid") : outOfRange ? t("dateInput.outOfRange") : "")
    }, [invalid, outOfRange, t])

    /* Hands the caller an event whose target is the native input holding the
       ISO value, so `e.target.value` reads the same as before. */
    const emit = (next: string, event: React.SyntheticEvent) => {
      const native = nativeRef.current
      if (!native || next === iso) return
      native.value = next
      const synthetic = Object.create(event, {
        target: { value: native },
        currentTarget: { value: native },
      }) as React.ChangeEvent<HTMLInputElement>
      onChange?.(synthetic)
    }

    const handleText = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      const digitsBefore = raw.slice(0, e.target.selectionStart ?? raw.length).replace(/\D/g, "").length
      const next = maskText(layout, raw, text)
      caret.current = (e.target.selectionStart ?? raw.length) >= raw.length ? next.length : caretAfterDigits(next, digitsBefore)
      setText(next)
      emit(textToIso(layout, type, next), e)
    }

    const handleNative = (e: React.ChangeEvent<HTMLInputElement>) => {
      setText(isoToText(layout, type, e.target.value))
      emit(normalizeIso(type, e.target.value), e)
    }

    /* Leaving the field shows the caller's value in the locale's form: "17:45"
       typed in en-US becomes "05:45 PM", and a partial text the caller ignored
       (some keep the last complete time) goes back to that value. */
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (iso) setText(isoToText(layout, type, iso))
      onBlur?.(e)
    }

    const openPicker = () => {
      const native = nativeRef.current
      if (!native || disabled || readOnly) return
      try {
        native.showPicker()
      } catch {
        /* no showPicker (older Safari) or no permission: a click opens it there */
        native.focus()
        native.click()
      }
    }

    const Icon = type === "time" ? Clock : Calendar
    const labels = {
      day: t("dateInput.day"),
      month: t("dateInput.month"),
      year: t("dateInput.year"),
      hour: t("dateInput.hour"),
      minute: t("dateInput.minute"),
    }

    return (
      <div
        className={cn(
          FIELD_BOX,
          "relative items-center gap-2 focus-within:ring-2 focus-within:ring-primary-200 focus-within:border-primary",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
        style={style}
      >
        <input
          {...rest}
          ref={textRef}
          type="text"
          inputMode={layout.hour12 ? "text" : "numeric"}
          autoComplete="off"
          value={text}
          onChange={handleText}
          onBlur={handleBlur}
          placeholder={placeholder || placeholderOf(layout, labels)}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        <input
          ref={nativeRef}
          type={type}
          name={name}
          value={iso}
          min={minIso}
          max={maxIso}
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          onChange={handleNative}
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={openPicker}
          disabled={disabled || readOnly}
          aria-label={type === "time" ? t("dateInput.openTimePicker") : t("dateInput.openPicker")}
          title={type === "time" ? t("dateInput.openTimePicker") : t("dateInput.openPicker")}
          className="relative shrink-0 rounded text-muted-foreground hover:text-foreground disabled:cursor-not-allowed"
        >
          <Icon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    )
  },
)
DateFieldInput.displayName = "DateFieldInput"
